import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { ThemingService } from '@catch/services';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MeterGroupModule } from 'primeng/metergroup';
import type { MeterItem } from 'primeng/types/metergroup';
import { CreditType } from '../../enums/credit-type.enum';
import { FontawesomeService } from '../../services/fontawesome.service';
import {
  selectCreditsLoaded,
  selectTotalCredits,
  selectTotalLeftCredits,
  selectTotalLeftCreditsOfType,
  selectTotalReservedCredits,
} from '../../store/credits/credits.reducer';
import {
  selectSubscription,
  selectSubscriptionLoaded,
} from '../../store/subscription/subscription.reducer';
import {
  buildCreditsMeterItems,
  creditsMeterMax,
  creditsUsedAmount,
  CREDITS_METER_COLORS,
} from './credits-meter.util';

export { CREDITS_METER_COLORS } from './credits-meter.util';

@Component({
  selector: 'app-credits-counter',
  imports: [
    DecimalPipe,
    MeterGroupModule,
    TranslocoPipe,
    NgxSkeletonLoaderModule,
    FontAwesomeModule,
  ],
  templateUrl: './credits-counter.component.html',
  styleUrl: './credits-counter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.compact]': 'compact()',
    '[class.show-legend]': 'showLegend()',
  },
})
export class CreditsCounterComponent {
  private readonly store = inject(Store);
  private readonly transloco = inject(TranslocoService);
  public readonly themingService = inject(ThemingService);
  public readonly fontawesomeService = inject(FontawesomeService);

  size = input<'sm' | 'md' | 'lg'>('md');
  showLabel = input(true);
  showLegend = input(false);
  showArrow = input(false);
  compact = input(true);

  subscription = this.store.selectSignal(selectSubscription);
  subscriptionLoaded = this.store.selectSignal(selectSubscriptionLoaded);
  creditsLoaded = this.store.selectSignal(selectCreditsLoaded);
  totalCredits = this.store.selectSignal(selectTotalCredits);
  totalLeftCredits = this.store.selectSignal(selectTotalLeftCredits);
  leftSubscriptionCredits = this.store.selectSignal(
    selectTotalLeftCreditsOfType(CreditType.BASIC),
  );
  leftExtraCredits = this.store.selectSignal(
    selectTotalLeftCreditsOfType(CreditType.ADDITIONAL),
  );
  totalReservedCredits = this.store.selectSignal(selectTotalReservedCredits);

  isLoading = computed(
    () => !this.subscriptionLoaded() || !this.creditsLoaded(),
  );

  skeletonAnimation = computed(() =>
    this.themingService.theme === 'dark' ? 'progress-dark' : 'progress',
  );

  usedCredits = computed(() =>
    creditsUsedAmount(
      this.totalCredits(),
      this.totalLeftCredits(),
      this.totalReservedCredits(),
    ),
  );

  meterMax = computed(() =>
    creditsMeterMax(
      this.totalLeftCredits(),
      this.totalReservedCredits(),
      this.usedCredits(),
      this.totalCredits(),
    ),
  );

  meterValue = computed((): MeterItem[] =>
    buildCreditsMeterItems({
      leftSubscription: this.leftSubscriptionCredits(),
      leftExtra: this.leftExtraCredits(),
      reserved: this.totalReservedCredits(),
      used: this.usedCredits(),
      translate: (key) => this.transloco.translate(key),
    }),
  );
}
